import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import clsx from 'clsx';
import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { Breadcrumb } from '@domains/breadcrumbs';
import { CategoriesTree, CategoryTreeRootItem } from '@domains/categories';
import { Seo } from '@domains/seo';
import { CategoryPageProps } from '@domains/view';

import { useCommonData } from '@stores/global-data';
import {
    $isProductsListInitializing,
    $productsList,
    initProductsList,
} from '@stores/products-list';

import { findCategoryParents } from '@utils/category/category';
import { NewProductsPageSlug, PromoPageSlug } from '@utils/constants';
import { APP_URL } from '@utils/env';
import { isValueSet } from '@utils/guards';
import { makeCatalogRoute, makeCategoryRoute, makeSearchRoute } from '@utils/route';
import { newProductsCatalogSeo, promoCatalogSeo } from '@utils/seo';
import { applyTypograf } from '@utils/typograf';
import { pluralize, replaceSpecialCharacters } from '@utils/utils';

import { useProductListFilters } from '@hooks/products-list-fiters';

import { DefaultLayout } from '@layouts/DefaultLayout/DefaultLayout';

import { CategoryDropdown } from '@components/catalog/CategoryDropdown/CategoryDropdown';
import { CategoryFilterChips } from '@components/catalog/CategoryFilterChips/CategoryFilterChips';
import { CategoryFiltersDrawer } from '@components/catalog/CategoryFiltersDrawer/CategoryFiltersDrawer';
import { CategoryFiltersDrawerButton } from '@components/catalog/CategoryFiltersDrawerButton/CategoryFiltersDrawerButton';
import { CategorySidebar } from '@components/catalog/CategorySidebar/CategorySidebar';
import { CategorySortingSelect } from '@components/catalog/CategorySortingSelect/CategorySortingSelect';
import { Breadcrumbs } from '@components/common/Breadcrumbs/Breadcrumbs';
import { Container } from '@components/common/Container/Container';
import { InformingBlock } from '@components/common/InformingBlock/InformingBlock';
import { Pagination } from '@components/common/Pagination/Pagination';
import { ProductGrid } from '@components/common/ProductGrid/ProductGrid';
import { Spinner } from '@components/common/Spinner/Spinner';

import styles from './CategoryView.module.scss';

const searchBreadcrumbs: Breadcrumb[] = [
    {
        caption: 'Результаты поиска',
    },
];

const promoBreadcrumbs: Breadcrumb[] = [
    {
        caption: 'Акции',
    },
];

const newProductsBreadcrumbs: Breadcrumb[] = [
    {
        caption: 'Новинки',
    },
];

const catalogBreadcrumb: Breadcrumb = {
    caption: 'Каталог',
    url: makeCatalogRoute(),
};

export const CategoryView: NextPage<CategoryPageProps> = (props) => {
    const { searchQuery, data, categorySlug } = props;
    const { total, categories, page_size, items, page } = data;

    const {
        settings: { seo: commonSeo },
    } = useCommonData();

    const [isFiltersDrawerOpen, setFiltersDrawerOpen] = useState(false);

    const isProductsListInitializing = useUnit($isProductsListInitializing);
    const initProductsListFn = useUnit(initProductsList);
    const { source } = useUnit($productsList);

    const { filtersChips, discardFilters } = useProductListFilters();

    const router = useRouter();
    const isPromoPage = categorySlug === PromoPageSlug;
    const isNewProductsPage = categorySlug === NewProductsPageSlug;

    const isInSearchMode = searchQuery !== null;
    const hasResults = total > 0;
    const hasAppliedFilters = filtersChips.length > 0;
    const isSearchQueryEmptyState = isInSearchMode && !hasResults && !hasAppliedFilters;

    const currentCategory = categories.find((item) => item.slug === categorySlug);
    const categoryId = currentCategory?.id ?? null;
    const categorySeo = currentCategory?.seo;
    const categoryTitle = useMemo(() => {
        if (isPromoPage) {
            return 'Акции';
        }

        if (isNewProductsPage) {
            return 'Новинки';
        }

        if (!isValueSet(currentCategory)) return 'Категория';

        return replaceSpecialCharacters(currentCategory.name);
    }, [currentCategory, isPromoPage, isNewProductsPage]);
    const title = isInSearchMode ? `Результаты поиска «${searchQuery}»` : categoryTitle;

    let seo: Seo | { title: string; siteName: string | null } | undefined;
    if (isInSearchMode) {
        seo = { title: `Поиск по запросу «${searchQuery}»`, siteName: commonSeo.title };
    } else if (isPromoPage) {
        seo = promoCatalogSeo;
    } else if (isNewProductsPage) {
        seo = newProductsCatalogSeo;
    } else {
        seo = categorySeo;
    }

    const formattedResultCount = `Найдено ${total} ${pluralize(total, [
        'товар',
        'товара',
        'товаров',
    ])}`;

    const breadcrumbs: Breadcrumb[] = useMemo(() => {
        if (isInSearchMode) return searchBreadcrumbs;

        if (isPromoPage) return promoBreadcrumbs;

        if (isNewProductsPage) return newProductsBreadcrumbs;

        if (categoryId !== null) {
            const parentCategories = findCategoryParents(categoryId, categories);
            const parentCategoriesLength = parentCategories.length;

            if (parentCategoriesLength > 1) {
                const mappedParentCategories: Breadcrumb[] = parentCategories.map(
                    ({ name, slug }, index) => ({
                        caption: replaceSpecialCharacters(name),
                        url:
                            index === parentCategoriesLength - 1
                                ? undefined
                                : makeCategoryRoute(slug),
                    }),
                );
                return [catalogBreadcrumb, ...mappedParentCategories];
            }
        }

        return [catalogBreadcrumb, { caption: title }];
    }, [isPromoPage, isNewProductsPage, isInSearchMode, categories, categoryId, title]);

    const categoriesTree: CategoriesTree = useMemo(() => {
        if (isInSearchMode) return [];

        const tree: CategoriesTree = categories
            .filter(({ parent_id }) => parent_id === categoryId)
            .map(
                (category): CategoryTreeRootItem => ({
                    ...category,
                    subcategories: categories.filter(({ parent_id }) => parent_id === category.id),
                }),
            );
        tree.source = source;

        return tree;
    }, [source, categories, categoryId, isInSearchMode]);

    const handleFiltersDrawerOpenChange = (isOpen: boolean) => {
        setFiltersDrawerOpen(isOpen);
    };

    const handleFiltersDrawerButtonClick = () => {
        setFiltersDrawerOpen((isOpen) => !isOpen);
    };

    useEffect(() => {
        initProductsListFn(props);
    }, [initProductsListFn, props]);

    const canonicalCategorySlug = currentCategory?.slug ?? null;
    const canonicalPath =
        !isInSearchMode && !isPromoPage && !isNewProductsPage && canonicalCategorySlug !== null
            ? `${APP_URL}/catalog/${canonicalCategorySlug}/`
            : undefined;

    return (
        <DefaultLayout
            {...seo}
            isWithDefaultSuffix={isInSearchMode || isPromoPage || isNewProductsPage}
            canonicalPath={canonicalPath}
        >
            {isProductsListInitializing && (
                <Container classes={{ root: styles.spinnerContainer }}>
                    <Spinner size="large" />
                </Container>
            )}

            {!isProductsListInitializing &&
                !hasResults &&
                !hasAppliedFilters &&
                !isSearchQueryEmptyState && (
                    <Container>
                        <InformingBlock
                            icon="empty"
                            title={{
                                text: 'Ой, ничего не\u00a0найдено :(',
                                element: 'h2',
                                marginBottom: 16,
                            }}
                            buttons={[
                                {
                                    variant: 'green',
                                    children: 'Перейдите в каталог',
                                    link: makeCatalogRoute(),
                                },
                                null,
                            ]}
                            className={styles.emptyState}
                        />
                    </Container>
                )}

            {!isProductsListInitializing &&
                (hasResults || hasAppliedFilters || isSearchQueryEmptyState) && (
                    <Container
                        classes={{
                            root: clsx(
                                styles.container,
                                isSearchQueryEmptyState && styles.isSearchEmptyState,
                            ),
                        }}
                    >
                        <Breadcrumbs
                            items={breadcrumbs}
                            isWithHomeCrumb
                            className={styles.breadcrumbs}
                        />

                        <div className={styles.header}>
                            <h1 className={clsx(styles.title, !isInSearchMode && styles.hasMargin)}>
                                {title}
                            </h1>
                            {isInSearchMode && (
                                <h2 className={styles.subtitle}>{formattedResultCount}</h2>
                            )}

                            {!isSearchQueryEmptyState && (
                                <Fragment>
                                    <CategoryDropdown
                                        categoriesTree={categoriesTree}
                                        className={styles.categoryDropdown}
                                    />
                                    <CategoryFiltersDrawerButton
                                        isActive={isFiltersDrawerOpen}
                                        onClick={handleFiltersDrawerButtonClick}
                                        className={styles.filtersDrawerButton}
                                    />
                                    <CategorySortingSelect className={styles.sortingSelect} />
                                </Fragment>
                            )}
                        </div>

                        {/* Chips */}
                        {!isSearchQueryEmptyState && (
                            <div
                                className={clsx(
                                    styles.filterChips,
                                    filtersChips.length > 0 && styles.hasMargin,
                                )}
                            >
                                {filtersChips.length > 0 && <CategoryFilterChips />}
                            </div>
                        )}

                        {/* Filters sidebar */}
                        {!isSearchQueryEmptyState && (
                            <CategorySidebar
                                categoriesTree={categoriesTree}
                                className={styles.sidebar}
                            />
                        )}

                        {/* Products */}
                        {hasResults && (
                            <ProductGrid
                                products={items}
                                variant="fourColumns"
                                className={styles.productGrid}
                                priceDigit={0}
                            />
                        )}

                        {/* Category empty state  */}
                        {!hasResults && hasAppliedFilters && (
                            <InformingBlock
                                icon="empty"
                                title={{
                                    text: 'Товаров с\u00a0такими характеристиками не\u00a0нашлось :(',
                                    element: 'h2',
                                    marginBottom: 16,
                                }}
                                description={{
                                    text: applyTypograf(
                                        'Сбросьте фильтры или воспользуйтесь поиском',
                                    ),
                                    marginBottom: 24,
                                    color: 'muted',
                                    size: 16,
                                }}
                                buttons={[
                                    {
                                        variant: 'green',
                                        children: 'Очистить фильтры',
                                        onClick: discardFilters,
                                    },
                                    null,
                                ]}
                                className={styles.productGridEmpty}
                            />
                        )}

                        {/* Search empty state  */}
                        {isInSearchMode && !hasResults && !hasAppliedFilters && (
                            <InformingBlock
                                title={{
                                    text: 'Ой, ничего не\u00a0найдено :(',
                                    element: 'h2',
                                    marginBottom: 16,
                                }}
                                icon="search"
                                buttons={[
                                    {
                                        variant: 'green',
                                        children: 'Перейдите в каталог',
                                        link: makeCatalogRoute(),
                                    },
                                    null,
                                ]}
                                className={styles.searchEmptyState}
                            />
                        )}

                        {hasResults && total > page_size && (
                            <Pagination
                                total={total}
                                pageSize={page_size}
                                currentPage={page}
                                className={styles.Pagination}
                                options={{
                                    currentPath:
                                        !isInSearchMode && categorySlug !== null
                                            ? makeCategoryRoute(categorySlug)
                                            : makeSearchRoute(),
                                    currentQuery: { ...router.query, idSlug: undefined },
                                }}
                            />
                        )}
                    </Container>
                )}

            <CategoryFiltersDrawer
                isOpen={isFiltersDrawerOpen}
                onOpenChange={handleFiltersDrawerOpenChange}
            />
        </DefaultLayout>
    );
};
